import { useEffect, useState } from "react";

/**
 * Sticky elements of a record screen that pin at the top of the record scroll
 * view (`.main__content`): the page header (breadcrumbs, title, action buttons)
 * and the record tab bar. A sticky bar further down the page (e.g. the Logs
 * tab's "Go to newest" bar) must pin BELOW them, else it sits behind them.
 */
export const STICKY_TOP_OCCLUDERS = [".page-header-sticky", ".state-tabs"];

export interface StickyOccluder {
    position: string;
    top: string;
    height: number;
}

/**
 * The lowest edge, relative to the scroller top, at which the given elements
 * sit once pinned: `top` + height for each `position: sticky` element.
 * Non-sticky elements scroll away and do not occlude, so they count as 0.
 */
export const pinnedOccluderBottom = (occluders: StickyOccluder[]): number =>
    occluders.reduce((max, o) => {
        if (o.position !== "sticky") {
            return max;
        }
        const top = parseFloat(o.top);
        return Math.max(max, (Number.isFinite(top) ? top : 0) + o.height);
    }, 0);

/**
 * Sticky `top` (px) for a bar inside `getScroller()` so it pins just below the
 * screen's own sticky header stack. Re-measured on scroll and resize, because
 * the page header's description collapses as the page scrolls. 0 when the
 * scroller has no sticky header (e.g. the workspace-wide Logs screen).
 */
export const useStickyTopOffset = (getScroller: () => HTMLElement | null): number => {
    const [offset, setOffset] = useState(0);
    // The scroller resolves only once the list is mounted; check after every
    // render and (re)attach when it appears or changes. The equality guard stops
    // the update chain, so the missing dependency list is intended.
    const [scroller, setScroller] = useState<HTMLElement | null>(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const current = getScroller();
        if (current !== scroller) {
            setScroller(current);
        }
    });

    useEffect(() => {
        if (!scroller) {
            return;
        }
        const measure = () => {
            const occluders: StickyOccluder[] = [];
            STICKY_TOP_OCCLUDERS.forEach((selector) => {
                scroller.querySelectorAll<HTMLElement>(selector).forEach((el) => {
                    const cs = getComputedStyle(el);
                    occluders.push({
                        position: cs.position,
                        top: cs.top,
                        height: el.offsetHeight,
                    });
                });
            });
            const next = Math.round(pinnedOccluderBottom(occluders));
            setOffset((prev) => (prev === next ? prev : next));
        };
        measure();
        scroller.addEventListener("scroll", measure, { passive: true });
        window.addEventListener("resize", measure);
        return () => {
            scroller.removeEventListener("scroll", measure);
            window.removeEventListener("resize", measure);
        };
    }, [scroller]);

    return offset;
};
